import { useState, useMemo } from "react";
import { useAuth } from "@/_core/hooks/useAuth";
import { trpc } from "@/lib/trpc";
import { safeFormat } from "@/lib/dateUtils";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Link } from "wouter";
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  ArrowLeft,
  Calendar,
} from "lucide-react";
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  addMonths,
  addWeeks,
  isSameDay,
  isSameMonth,
  isToday,
  startOfDay,
  endOfDay,
} from "date-fns";
import { ptBR } from "date-fns/locale";
import { BeautyChatBot } from "@/components/BeautyChatBot";

type ViewMode = "day" | "week" | "month";

const STATUS_COLORS: Record<string, string> = {
  pending: "bg-yellow-100 text-yellow-800 border-yellow-200",
  confirmed: "bg-green-100 text-green-800 border-green-200",
  cancelled: "bg-red-100 text-red-800 border-red-200",
  completed: "bg-blue-100 text-blue-800 border-blue-200",
  no_show: "bg-gray-100 text-gray-800 border-gray-200",
};

const STATUS_LABELS: Record<string, string> = {
  pending: "Pendente",
  confirmed: "Confirmado",
  cancelled: "Cancelado",
  completed: "Concluído",
  no_show: "Não compareceu",
};

export default function CalendarPage() {
  const { user } = useAuth();
  const [viewMode, setViewMode] = useState<ViewMode>("week");
  const [currentDate, setCurrentDate] = useState(new Date());

  const dateRange = useMemo(() => {
    if (viewMode === "day") {
      return { startDate: startOfDay(currentDate), endDate: endOfDay(currentDate) };
    } else if (viewMode === "week") {
      const start = startOfWeek(currentDate, { weekStartsOn: 1 });
      return { startDate: start, endDate: endOfWeek(currentDate, { weekStartsOn: 1 }) };
    } else {
      const start = startOfMonth(currentDate);
      return {
        startDate: startOfWeek(start, { weekStartsOn: 1 }),
        endDate: endOfWeek(endOfMonth(currentDate), { weekStartsOn: 1 }),
      };
    }
  }, [viewMode, currentDate]);

  // Stable query input — spread creates a new object every render causing infinite re-fetches
  const apptQueryInput = useMemo(
    () => ({ salonId: 1, ...dateRange }),
    [dateRange]
  );
  const { data: appointments } = trpc.appointments.getByDateRangePublic.useQuery(apptQueryInput);
  // Stable query inputs — objetos literais inline causam re-fetches infinitos
  const calProfsInput = useMemo(() => ({ salonId: 1 }), []);
  const calServicesInput = useMemo(() => ({ salonId: 1 }), []);
  const { data: professionals } = trpc.professionals.list.useQuery(calProfsInput);
  const { data: services } = trpc.services.list.useQuery(calServicesInput);

  const navigate = (dir: 1 | -1) => {
    if (viewMode === "day") setCurrentDate((d) => addDays(d, dir));
    else if (viewMode === "week") setCurrentDate((d) => addWeeks(d, dir));
    else setCurrentDate((d) => addMonths(d, dir));
  };

  const getTitle = () => {
    if (viewMode === "day") return format(currentDate, "EEEE, d 'de' MMMM yyyy", { locale: ptBR });
    if (viewMode === "week") {
      const start = startOfWeek(currentDate, { weekStartsOn: 1 });
      const end = endOfWeek(currentDate, { weekStartsOn: 1 });
      return `${format(start, "d MMM", { locale: ptBR })} – ${format(end, "d MMM yyyy", { locale: ptBR })}`;
    }
    return format(currentDate, "MMMM yyyy", { locale: ptBR });
  };

  const getAppointmentsForDay = (day: Date) =>
    appointments?.filter((a) => isSameDay(new Date(a.startTime), day)) ?? [];

  const getProfName = (id: number) => professionals?.find((p) => p.id === id)?.name ?? "—";
  const getServiceName = (id: number) => services?.find((s) => s.id === id)?.name ?? "—";
  const getProfColor = (id: number) => professionals?.find((p) => p.id === id)?.color ?? "#8B6914";

  const HOURS = Array.from({ length: 13 }, (_, i) => i + 8); // 8–20

  const weekDays = useMemo(() => {
    const start = startOfWeek(currentDate, { weekStartsOn: 1 });
    return Array.from({ length: 7 }, (_, i) => addDays(start, i));
  }, [currentDate]);

  const monthDays = useMemo(() => {
    const start = startOfWeek(startOfMonth(currentDate), { weekStartsOn: 1 });
    const end = endOfWeek(endOfMonth(currentDate), { weekStartsOn: 1 });
    const days: Date[] = [];
    let d = start;
    while (d <= end) { days.push(d); d = addDays(d, 1); }
    return days;
  }, [currentDate]);

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-border/50 shadow-sm">
        <div className="container flex items-center justify-between h-16">
          <Link href="/">
            <div className="flex items-center gap-2 cursor-pointer">
              <ArrowLeft className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground hidden sm:block">Voltar</span>
            </div>
          </Link>
          <h1 className="font-[Cormorant_Garamond,serif] text-xl font-semibold">Calendário</h1>
          <Link href="/booking">
            <Button size="sm" className="bg-primary text-primary-foreground">
              <Plus className="w-4 h-4 mr-1" /> Agendar
            </Button>
          </Link>
        </div>
      </nav>

      <div className="flex-1 pt-16 flex flex-col">
        {/* Calendar Controls */}
        <div className="container py-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <button onClick={() => navigate(-1)} className="p-1.5 rounded-lg hover:bg-secondary transition-colors">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <h2 className="text-base font-semibold text-foreground capitalize min-w-[180px] text-center">
              {getTitle()}
            </h2>
            <button onClick={() => navigate(1)} className="p-1.5 rounded-lg hover:bg-secondary transition-colors">
              <ChevronRight className="w-4 h-4" />
            </button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentDate(new Date())}
              className="ml-2 text-xs"
            >
              Hoje
            </Button>
          </div>

          <div className="flex rounded-lg border border-border/50 overflow-hidden">
            {(["day", "week", "month"] as ViewMode[]).map((v) => (
              <button
                key={v}
                onClick={() => setViewMode(v)}
                className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                  viewMode === v ? "bg-primary text-primary-foreground" : "hover:bg-secondary text-muted-foreground"
                }`}
              >
                {v === "day" ? "Dia" : v === "week" ? "Semana" : "Mês"}
              </button>
            ))}
          </div>
        </div>

        {/* Month View */}
        {viewMode === "month" && (
          <div className="container pb-8 flex-1">
            <div className="grid grid-cols-7 mb-1">
              {["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"].map((d) => (
                <div key={d} className="text-center text-xs font-medium text-muted-foreground py-2">{d}</div>
              ))}
            </div>
            <div className="grid grid-cols-7 border-l border-t border-border/30 rounded-lg overflow-hidden">
              {monthDays.map((day) => {
                const dayAppts = getAppointmentsForDay(day);
                return (
                  <div
                    key={day.toISOString()}
                    className={`min-h-[90px] border-r border-b border-border/30 p-1.5 ${
                      !isSameMonth(day, currentDate) ? "bg-secondary/30" : "bg-card"
                    } ${isToday(day) ? "bg-primary/5" : ""}`}
                  >
                    <span
                      className={`text-xs font-medium inline-flex w-6 h-6 items-center justify-center rounded-full ${
                        isToday(day) ? "bg-primary text-primary-foreground" : "text-foreground"
                      } ${!isSameMonth(day, currentDate) ? "text-muted-foreground/50" : ""}`}
                    >
                      {format(day, "d")}
                    </span>
                    <div className="mt-1 space-y-0.5">
                      {dayAppts.slice(0, 2).map((a) => (
                        <div
                          key={a.id}
                          className={`text-[10px] px-1 py-0.5 rounded truncate border ${STATUS_COLORS[a.status]}`}
                          title={getServiceName(a.serviceId)}
                        >
                          {safeFormat(a.startTime, "HH:mm")} {getServiceName(a.serviceId)}
                        </div>
                      ))}
                      {dayAppts.length > 2 && (
                        <div className="text-[10px] text-muted-foreground pl-1">+{dayAppts.length - 2} mais</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Week View */}
        {viewMode === "week" && (
          <div className="container pb-8 flex-1 overflow-x-auto">
            <div className="min-w-[600px]">
              {/* Day headers */}
              <div className="grid grid-cols-8 mb-1 sticky top-16 bg-background z-10">
                <div className="text-xs text-muted-foreground p-2" />
                {weekDays.map((day) => (
                  <div key={day.toISOString()} className={`text-center py-2 ${isToday(day) ? "text-primary" : ""}`}>
                    <div className="text-[10px] uppercase text-muted-foreground">{format(day, "EEE", { locale: ptBR })}</div>
                    <div className={`text-sm font-semibold mt-0.5 inline-flex w-7 h-7 items-center justify-center rounded-full mx-auto ${isToday(day) ? "bg-primary text-primary-foreground" : "text-foreground"}`}>
                      {format(day, "d")}
                    </div>
                  </div>
                ))}
              </div>

              {/* Time grid */}
              <div className="grid grid-cols-8 border-l border-t border-border/30 rounded-lg overflow-hidden">
                {HOURS.map((hour) => (
                  <>
                    <div key={`h-${hour}`} className="border-r border-b border-border/30 p-1 text-right">
                      <span className="text-[10px] text-muted-foreground">{hour}:00</span>
                    </div>
                    {weekDays.map((day) => {
                      const dayAppts = getAppointmentsForDay(day).filter(
                        (a) => new Date(a.startTime).getHours() === hour
                      );
                      return (
                        <div
                          key={`${day.toISOString()}-${hour}`}
                          className={`border-r border-b border-border/30 min-h-[50px] p-0.5 ${isToday(day) ? "bg-primary/3" : "bg-card"}`}
                        >
                          {dayAppts.map((a) => (
                            <div
                              key={a.id}
                              className={`text-[10px] px-1.5 py-1 rounded mb-0.5 border ${STATUS_COLORS[a.status]}`}
                              style={{ borderLeftColor: getProfColor(a.professionalId), borderLeftWidth: 3 }}
                            >
                              <div className="font-medium truncate">{getServiceName(a.serviceId)}</div>
                              <div className="text-[9px] opacity-70">{getProfName(a.professionalId)}</div>
                            </div>
                          ))}
                        </div>
                      );
                    })}
                  </>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Day View */}
        {viewMode === "day" && (
          <div className="container pb-8 flex-1">
            <div className="max-w-lg mx-auto">
              <div className="border border-border/30 rounded-lg overflow-hidden">
                {HOURS.map((hour) => {
                  const hourAppts = getAppointmentsForDay(currentDate).filter(
                    (a) => new Date(a.startTime).getHours() === hour
                  );
                  return (
                    <div key={hour} className="flex border-b border-border/30 last:border-b-0">
                      <div className="w-16 shrink-0 p-2 text-right border-r border-border/30 bg-secondary/20">
                        <span className="text-xs text-muted-foreground">{hour}:00</span>
                      </div>
                      <div className="flex-1 min-h-[60px] p-1.5 space-y-1">
                        {hourAppts.map((a) => (
                          <div
                            key={a.id}
                            className={`px-3 py-2 rounded-lg border ${STATUS_COLORS[a.status]}`}
                            style={{ borderLeftColor: getProfColor(a.professionalId), borderLeftWidth: 4 }}
                          >
                            <div className="flex items-center justify-between">
                              <span className="font-medium text-sm">{getServiceName(a.serviceId)}</span>
                              <Badge variant="outline" className={`text-xs ${STATUS_COLORS[a.status]}`}>
                                {STATUS_LABELS[a.status]}
                              </Badge>
                            </div>
                            <div className="text-xs text-muted-foreground mt-0.5">
                              {getProfName(a.professionalId)} · {safeFormat(a.startTime, "HH:mm")}–{safeFormat(a.endTime, "HH:mm")}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>

      <BeautyChatBot userId={user?.id} />
    </div>
  );
}
